import { useEffect, useRef, useState, useCallback } from "react";
import { X, ZoomIn } from "lucide-react";

const BOX = 272;   // on-screen crop viewport (square)
const OUT = 512;   // exported image size

// Simple square crop: pan (drag) + zoom, exports a centered 512x512 JPEG blob.
export default function AvatarCropper({ file, onCancel, onCropped }) {
  const [url, setUrl] = useState(null);
  const [img, setImg] = useState(null);
  const [cover, setCover] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [off, setOff] = useState({ x: 0, y: 0 });
  const [saving, setSaving] = useState(false);
  const drag = useRef(null);

  useEffect(() => {
    const u = URL.createObjectURL(file);
    setUrl(u);
    const image = new Image();
    image.onload = () => {
      const c = Math.max(BOX / image.naturalWidth, BOX / image.naturalHeight);
      setImg(image); setCover(c); setZoom(1);
      const dw = image.naturalWidth * c, dh = image.naturalHeight * c;
      setOff({ x: (BOX - dw) / 2, y: (BOX - dh) / 2 });
    };
    image.src = u;
    return () => URL.revokeObjectURL(u);
  }, [file]);

  const clamp = useCallback((o, z) => {
    if (!img) return o;
    const dw = img.naturalWidth * cover * z, dh = img.naturalHeight * cover * z;
    return {
      x: Math.min(0, Math.max(BOX - dw, o.x)),
      y: Math.min(0, Math.max(BOX - dh, o.y)),
    };
  }, [img, cover]);

  const onZoom = (z) => {
    setZoom(z);
    setOff((o) => clamp(o, z));
  };

  const start = (e) => {
    const p = e.touches ? e.touches[0] : e;
    drag.current = { px: p.clientX, py: p.clientY, ox: off.x, oy: off.y };
  };
  const move = (e) => {
    if (!drag.current) return;
    const p = e.touches ? e.touches[0] : e;
    const nx = drag.current.ox + (p.clientX - drag.current.px);
    const ny = drag.current.oy + (p.clientY - drag.current.py);
    setOff(clamp({ x: nx, y: ny }, zoom));
  };
  const end = () => { drag.current = null; };

  const save = async () => {
    if (!img) return;
    setSaving(true);
    const canvas = document.createElement("canvas");
    canvas.width = OUT; canvas.height = OUT;
    const ctx = canvas.getContext("2d");
    const ratio = OUT / BOX;
    const dw = img.naturalWidth * cover * zoom * ratio;
    const dh = img.naturalHeight * cover * zoom * ratio;
    ctx.fillStyle = "#000";
    ctx.fillRect(0, 0, OUT, OUT);
    ctx.drawImage(img, off.x * ratio, off.y * ratio, dw, dh);
    canvas.toBlob((blob) => {
      setSaving(false);
      if (blob) onCropped(blob);
    }, "image/jpeg", 0.9);
  };

  const dw = img ? img.naturalWidth * cover * zoom : BOX;
  const dh = img ? img.naturalHeight * cover * zoom : BOX;

  return (
    <div className="fixed inset-0 z-[130] flex items-center justify-center bg-black/80 p-4" data-testid="avatar-cropper">
      <div className="w-full max-w-sm overflow-hidden rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-surface)] shadow-2xl">
        <div className="flex items-center justify-between border-b border-[var(--border-subtle)] px-4 py-3">
          <h2 className="font-head text-lg text-[var(--text-primary)]">Crop your photo</h2>
          <button onClick={onCancel} className="text-[var(--text-secondary)] hover:text-[var(--text-primary)]" data-testid="avatar-cropper-cancel" aria-label="Cancel"><X size={18} /></button>
        </div>
        <div className="flex flex-col items-center gap-4 p-4">
          <div
            className="relative overflow-hidden rounded-full border-2 border-[var(--accent)] bg-black touch-none select-none"
            style={{ width: BOX, height: BOX, cursor: "grab" }}
            onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
            onTouchStart={start} onTouchMove={move} onTouchEnd={end}
            data-testid="avatar-crop-viewport"
          >
            {url && (
              <img
                src={url} alt="crop" draggable={false}
                style={{ position: "absolute", left: off.x, top: off.y, width: dw, height: dh, maxWidth: "none" }}
              />
            )}
          </div>
          <div className="flex w-full items-center gap-3">
            <ZoomIn size={18} className="text-[var(--text-secondary)]" />
            <input type="range" min="1" max="3" step="0.01" value={zoom}
              onChange={(e) => onZoom(parseFloat(e.target.value))}
              className="w-full accent-[var(--accent)]" data-testid="avatar-crop-zoom" />
          </div>
          <div className="flex w-full gap-2">
            <button onClick={onCancel} className="flex-1 rounded-[var(--card-radius)] border border-[var(--border-subtle)] px-4 py-2.5 text-sm font-semibold text-[var(--text-primary)] hover:border-[var(--accent)]" data-testid="avatar-crop-cancel-btn">Cancel</button>
            <button onClick={save} disabled={saving || !img} className="flex-1 rounded-[var(--card-radius)] bg-[var(--accent)] px-4 py-2.5 text-sm font-bold uppercase tracking-wide text-[var(--accent-contrast)] hover:bg-[var(--accent-hover)] disabled:opacity-60" data-testid="avatar-crop-save-btn">{saving ? "Saving…" : "Save photo"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
